import {Helmet} from 'react-helmet-async';
import {Link as RouterLink} from 'react-router-dom';
// @mui
import {alpha, Box, Button, Card, CardContent, Container, Grid, Link, Stack, Typography} from '@mui/material';
import {CheckRounded, ExpandMore} from '@mui/icons-material';
// ----------------------------------------------------------------------

const CONTACT_EMAIL = 'info@omniumai.com';

const plans = [{
    name: 'Small Companies',
    price: '€200',
    priceSuffix: '/month',
    billing: 'Early-bird price · billed annually · €2,400/year',
    isEarlyBird: true,
    description: 'For smaller companies with focused teams and straightforward collaboration and information-management needs.',
    capabilities: ['Access for an agreed small-company team', 'Collaboration for focused product-development workflows', 'Chat and email support',],
}, // {
    //     name: 'Medium and Large Companies',
    //     price: 'To be announced',
    //     priceSuffix: '',
    //     billing: 'Pricing details to be announced',
    //     description: 'For companies with larger teams, multiple users and cross-departmental use of the platform.',
    //     capabilities: [
    //         'Multi-user, cross-departmental use',
    //         'Custom integrations available',
    //         'Chat and email support',
    //     ],
    // },
    {
        name: 'Enterprise',
        price: 'Custom pricing',
        priceSuffix: '',
        billing: 'Annual agreement',
        description: 'For larger organisations that need customised infrastructure, stronger isolation and configuration for specific requirements.',
        capabilities: ['Custom infrastructure and configuration', 'On-premise, air-gapped or custom-cloud deployment', 'Dedicated support team',],
    },];

/*
Temporarily hidden comparison table.

const platformFeatures = [
    'Labeling',
    'FLM',
    'Regulatory Radar',
    'Product Briefs and Technical Specifications',
    'Regulatory',
    'Nutritional Calculator',
    'Admin Dashboard',
    'Import Tools',
    'AI Assistant',
];

const comparisonSections = [
    {
        label: 'Platform',
        rows: platformFeatures.map((label) => ({
            label,
            values: ['confirm', 'confirm', 'confirm', 'confirm'],
        })),
    },
    {
        label: 'Services and support',
        rows: [
            {
                label: 'Expert data preparation and formulation support',
                values: ['confirm', 'confirm', 'confirm', 'confirm'],
            },
            {
                label: 'Onboarding',
                values: ['€500', '€500', '€500', '€500'],
            },
            {
                label: 'Support',
                values: ['Chat and email', 'Chat and email', 'Chat and email', 'Dedicated team'],
            },
        ],
    },
    {
        label: 'Enterprise',
        rows: [
            {
                label: 'Custom integrations: ERP, LIMS and CRM',
                values: [false, false, true, true],
            },
            {
                label: 'On-premise, air-gapped deployment or custom cloud',
                values: [false, false, false, true],
            },
        ],
    },
];

const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
};

function Availability({value}) {
    if (value === true) {
        return (
            <Box sx={{display: 'inline-flex', color: 'primary.main'}}>
                <CheckRounded aria-hidden="true" fontSize="small"/>
                <Box component="span" sx={visuallyHidden}>Available</Box>
            </Box>
        );
    }

    if (value === false) {
        return (
            <Box sx={{display: 'inline-flex', color: 'text.disabled'}}>
                <Box component="span" sx={visuallyHidden}>Not available</Box>
            </Box>
        );
    }

    if (value === 'confirm') {
        return (
            <Box sx={{display: 'inline-flex', color: 'primary.main'}}>
                <CheckRounded aria-hidden="true" fontSize="small"/>
                <Box component="span" sx={visuallyHidden}>
                    Confirm availability
                </Box>
            </Box>
        );
    }

    return (
        <Typography variant="body2" color="text.primary">
            {value}
        </Typography>
    );
}

function ComparisonTable() {
    return (
        <TableContainer
            component={Box}
            role="region"
            aria-label="CibusAI plan comparison. Scroll horizontally to view all plans on smaller screens."
            tabIndex={0}
            sx={{
                maxWidth: '100%',
                overflowX: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.customShadows.card,
                '&:focus-visible': {
                    outline: '3px solid',
                    outlineColor: 'primary.light',
                    outlineOffset: 3,
                },
            }}
        >
            <Table
                aria-label="Compare CibusAI pricing plans"
                sx={{
                    minWidth: {xs: 860, sm: 960, md: 980},
                    tableLayout: 'fixed',
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                width: {xs: 180, sm: 260, md: 280},
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                            }}
                        >
                            Feature
                        </TableCell>
                        <TableCell align="center" sx={{width: 165}}>
                            Small Companies
                        </TableCell>
                        <TableCell align="center" sx={{width: 180}}>
                            Medium/Large Companies
                        </TableCell>
                        <TableCell align="center" sx={{width: 165}}>
                            Enterprise
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {comparisonSections.map((section) => ([
                        <TableRow key={`${section.label}-section`}>
                            <TableCell
                                colSpan={4}
                                sx={{
                                    py: 1.5,
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                    color: 'primary.dark',
                                    typography: 'overline',
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {section.label}
                            </TableCell>
                        </TableRow>,

                        ...section.rows.map((row) => (
                            <TableRow key={row.label} hover>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                        bgcolor: 'background.paper',
                                        fontWeight: 500,
                                    }}
                                >
                                    {row.label}
                                </TableCell>

                                {row.values.slice(1).map((value, index) => (
                                    <TableCell
                                        align="center"
                                        key={`${row.label}-${index}`}
                                    >
                                        <Availability value={value}/>
                                    </TableCell>
                                ))}
                            </TableRow>
                        )),
                    ]))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
*/

const faqs = [{
    question: 'Is my data secure?', answer: (<>
            Customer data is isolated and accessible only to authorised users. Customer data is not used to train AI models for other customers without explicit agreement. For more details consult our{' '}
            <Link component={RouterLink} to="/terms-of-service">
                Terms of Service
            </Link>
            .
        </>),
}, {
        question: 'Can we import our existing data?',
        answer: 'Yes. CibusAI can support the import of existing product, formulation and ingredient data, as well as documents such as technical datasheets and specifications. We can also provide custom integrations with ERPs.',
    },
    {
        question: 'Do we have to enter everything manually?',
        answer: 'No. CibusAI uses AI to extract and organise relevant information from documents, reducing manual work.',
    },
    {
        question: 'Can we export our data if we leave CibusAI?',
        answer: 'Yes. Product, formulation and associated documentation data can be exported. In the current version, exports are handled with support from the CibusAI team. CibusAI uses public identifiers to support data portability.',
    },
    {
        question: 'Are we locked into CibusAI once we start using it?',
        answer: 'No. Data portability is an important part of the CibusAI approach. Customers should stay because of the value CibusAI provides, not because their data is locked in. CibusAI uses public identifiers to support data portability.',
    },
    {
        question: 'How long can we test CibusAI?',
        answer: 'Trials typically last three months, but longer pilot periods can be negotiated if needed.',
    },
    {
        question: 'Is the €2,400/year early-bird price guaranteed?',
        answer: 'For early-bird customers, the €2,400/year price is guaranteed for two years after the first discounted year. Future features may involve additional costs.',
    },
    {
        question: 'Will CibusAI replace our R&D team?',
        answer: 'No. CibusAI is designed to support and augment R&D teams by organising information, automating tasks and assisting with formulation and analysis.',
    },
    {
        question: 'Can CibusAI integrate with our existing systems?',
        answer: 'Yes. CibusAI is designed to integrate with external systems, including ERP solutions. Custom integrations can be discussed with the CibusAI team.',
    },
    {
        question: 'What is the future of CibusAI?',
        answer: 'CibusAI is evolving towards an intelligent product development ecosystem that includes AI-assisted formulation, regulatory support, ingredient knowledge and advanced search and analysis.',
    },
    {
        question: 'What if OmniumAI stops operating?',
        answer: 'Data portability is a key part of CibusAI, allowing customers to retrieve their information if needed.',
    },
    {
        question: 'How do we know CibusAI will actually create value for us?',
        answer: 'That is the purpose of the trial. The goal is to test CibusAI within the customer’s real workflow and measure whether it improves efficiency, organisation and product development. The platform has been used by our partners in pilot projects.',
    },
];

function contactHref(planName) {
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`CibusAI ${planName} plan enquiry`)}`;
}

function PricingCard({plan}) {
    return (<Card
            variant="outlined"
            sx={{
                height: '100%',
                display: 'flex',
                borderColor: (theme) => (plan.isEarlyBird ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider),
                boxShadow: (theme) => (plan.isEarlyBird ? theme.customShadows.card : 'none'),
                transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
                '&:hover': {
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.42), boxShadow: (theme) => theme.customShadows.card, transform: 'translateY(-2px)',
                },
            }}
        >
            <CardContent
                sx={{
                    p: {xs: 3, md: 3.5}, display: 'flex', flexDirection: 'column', width: '100%',
                }}
            >
                <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                        mb: 2, minHeight: {lg: 68},
                    }}
                >
                    {plan.name}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 3, minHeight: {lg: 106},
                    }}
                >
                    {plan.description}
                </Typography>


                <Box
                    sx={{
                        mb: 0.75, minHeight: 53, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative', display: 'inline-flex',
                        }}
                    >
                        <Typography
                            variant="h3"
                            component="p"
                            sx={{
                                fontFamily: 'Barlow, sans-serif', fontWeight: 600, lineHeight: 1.2,
                            }}
                        >
                            {plan.price}
                        </Typography>

                        {plan.isEarlyBird && (<Box
                                sx={{
                                    position: 'absolute',
                                    px: 0.9,
                                    py: 0.35,
                                    borderRadius: 0.75,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    top: -8,
                                    left: 'calc(100% - 2px)',
                                    right: 'auto',
                                    transform: 'rotate(-4deg)',
                                    transformOrigin: 'left bottom',
                                    boxShadow: '0 2px 6px rgba(20, 50, 100, 0.14)',
                                    zIndex: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: 'block', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1, whiteSpace: 'nowrap',
                                    }}
                                >
                                    EARLY BIRD
                                </Typography>
                            </Box>)}
                    </Box>

                    {plan.priceSuffix && (<Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {plan.priceSuffix}
                        </Typography>)}
                </Box>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        mb: 2.5, minHeight: 36,
                    }}
                >
                    {plan.billing}
                </Typography>

                <Box
                    sx={{
                        mb: 3, p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1, borderRadius: 1.5, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06), border: '1px solid', borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
                    }}
                >
                    <CheckRounded
                        aria-hidden="true"
                        sx={{
                            mt: '2px', fontSize: 19, color: 'primary.main', flexShrink: 0,
                        }}
                    />

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'primary.dark', fontWeight: 600,
                        }}
                    >
                        Up to €500 in onboarding costs covered
                    </Typography>
                </Box>

                <Typography
                    variant="subtitle2"
                    sx={{mb: 1.5}}
                >
                    Plan highlights
                </Typography>

                <Stack
                    component="ul"
                    spacing={1.25}
                    sx={{
                        listStyle: 'none', p: 0, m: 0, mb: 3,
                    }}
                >
                    {plan.capabilities.map((capability) => (<Stack
                            component="li"
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            key={capability}
                        >
                            <CheckRounded
                                aria-hidden="true"
                                sx={{
                                    fontSize: 19, mt: '2px', color: 'primary.main', flexShrink: 0,
                                }}
                            />

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {capability}
                            </Typography>
                        </Stack>))}
                </Stack>

                <Button
                    fullWidth
                    variant="contained"
                    component="a"
                    href={contactHref(plan.name)}
                    sx={{
                        mt: 'auto', boxShadow: 'none',
                    }}
                >
                    Discuss this plan
                </Button>
            </CardContent>
        </Card>);
}

function FAQItem({question, answer}) {
    return (<Box
            component="details"
            sx={{
                borderBottom: '1px solid', borderColor: 'divider', '&:first-of-type': {
                    borderTop: '1px solid', borderColor: 'divider',
                }, '&[open] .faq-icon': {
                    transform: 'rotate(180deg)',
                },
            }}
        >
            <Box
                component="summary"
                sx={{
                    py: 2.5, px: {xs: 0, sm: 1}, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, cursor: 'pointer', listStyle: 'none', '&::-webkit-details-marker': {
                        display: 'none',
                    }, '&:focus-visible': {
                        outline: '3px solid', outlineColor: 'primary.light', outlineOffset: 2, borderRadius: 1,
                    },
                }}
            >
                <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{fontWeight: 600}}
                >
                    {question}
                </Typography>

                <ExpandMore
                    className="faq-icon"
                    aria-hidden="true"
                    sx={{
                        color: 'primary.main', flexShrink: 0, transition: 'transform 180ms ease',
                    }}
                />
            </Box>

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    px: {xs: 0, sm: 1}, pb: 3, maxWidth: 880,
                }}
            >
                {answer}
            </Typography>
        </Box>);
}

export default function PricingPage() {
    return (<>
            <Helmet>
                <title>CibusAI Pricing | Plans for Food Product Development Teams</title>

                <meta
                    name="description"
                    content="Explore CibusAI plans for small companies, larger teams and enterprise food product-development organisations."
                />
            </Helmet>

            <Box component="main" sx={{overflow: 'hidden'}}>
                <Container maxWidth="xl">
                    <Box
                        component="section"
                        sx={{
                            pt: {xs: 7, md: 11}, pb: {xs: 6, md: 8}, textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                mt: 1.5, mb: 3,
                            }}
                        >
                            Choose the plan that fits your team
                        </Typography>

                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                maxWidth: 760, mx: 'auto',
                            }}
                        >
                            CibusAI plans are designed around different team structures and product-development needs.
                            Explore the options below and contact us to confirm the right plan for your organisation.
                        </Typography>
                    </Box>
                </Container>

                <Container maxWidth="xl">
                    <Box
                        component="section"
                        aria-label="CibusAI pricing plans"
                        sx={{
                            pb: {xs: 7, md: 10},
                        }}
                    >
                        <Grid
                            container
                            spacing={3}
                            justifyContent="center"
                            alignItems="stretch"
                        >
                            {plans.map((plan) => (<Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    key={plan.name}
                                    sx={{
                                        maxWidth: {sm: '450px !important'}, flexBasis: {sm: '450px !important'},
                                    }}
                                >
                                    <PricingCard plan={plan}/>
                                </Grid>))}
                        </Grid>
                    </Box>

                    <Box
                        component="section"
                        aria-labelledby="individual-offer-heading"
                        sx={{
                            pb: {xs: 10, md: 13},
                        }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2), bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035), boxShadow: 'none',
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: {xs: 3, md: 5},
                                }}
                            >
                                <Grid
                                    container
                                    spacing={{xs: 4, md: 7}}
                                    alignItems="center"
                                >
                                    <Grid item xs={12} md={8}>

                                        <Typography
                                            id="individual-o

                                            ffer-heading"
                                            variant="h2"
                                            sx={{
                                                mt: 1.5, mb: 2,
                                            }}
                                        >
                                            Individual access
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{maxWidth: 760}}
                                        >
                                            An individual licence for professionals who want to test CibusAI independently. Where relevant, separate licensing or collaboration terms can be agreed on a case-by-case basis for individuals who wish to help introduce CibusAI
                                            to potential customers or support its commercial development.
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                p: {xs: 2.5, md: 3}, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                            }}
                                        >
                                            <Typography
                                                variant="h4"
                                                sx={{mb: 1}}
                                            >
                                                Individual licence
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{mb: 3}}
                                            >
                                                Negotiable pricing for an agreed period.
                                            </Typography>

                                            <Button
                                                fullWidth
                                                variant="contained"
                                                component="a"
                                                href={contactHref('Individual')}
                                                sx={{boxShadow: 'none'}}
                                            >
                                                Ask about this offer
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                </Container>

                {/*
                Temporarily hidden: feature comparison table.

                <Box
                    component="section"
                    aria-labelledby="compare-plans-heading"
                    sx={{
                        py: {xs: 9, md: 13},
                        bgcolor: 'background.neutral',
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Container maxWidth="xl">
                        <Box sx={{mb: 5, maxWidth: 740}}>
                            <Typography
                                id="compare-plans-heading"
                                variant="h2"
                                sx={{mb: 2}}
                            >
                                Compare plans
                            </Typography>

                            <Typography
                                variant="body1"
                                color="text.secondary"
                            >
                                Review the features and services included in each CibusAI plan.
                            </Typography>
                        </Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: {xs: 'block', md: 'none'},
                                mb: 1.5,
                            }}
                        >
                            Swipe horizontally to compare every plan.
                        </Typography>

                        <ComparisonTable/>
                    </Container>
                </Box>
                */}

                {/*
                Temporarily hidden: pricing notes.

                <Container maxWidth="xl">
                    <Box
                        component="section"
                        aria-labelledby="pricing-notes-heading"
                        sx={{
                            py: {xs: 9, md: 12},
                        }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                                boxShadow: 'none',
                                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035),
                            }}
                        >
                            <CardContent sx={{p: {xs: 3, md: 5}}}>
                                <Typography
                                    id="pricing-notes-heading"
                                    variant="h3"
                                    sx={{mb: 3}}
                                >
                                    Pricing notes
                                </Typography>

                                <Box
                                    component="ul"
                                    sx={{
                                        pl: 2.5,
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Typography component="li" variant="body2">
                                        Annual billing applies to the Small Companies and Medium and Large Companies plans.
                                    </Typography>

                                    <Typography component="li" variant="body2">
                                        Enterprise pricing is customised according to team size, infrastructure, deployment and integration requirements.
                                    </Typography>

                                    <Typography component="li" variant="body2">
                                        Pioneer customers may have the €500 onboarding fee included in their annual subscription.
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Container>
                */}

                <Container maxWidth="xl">
                    <Box
                        component="section"
                        aria-labelledby="pricing-faq-heading"
                        sx={{
                            pb: {xs: 10, md: 14},
                        }}
                    >
                        <Grid
                            container
                            spacing={{xs: 4, md: 8}}
                        >
                            <Grid item xs={12} md={4}>
                                <Typography
                                    variant="overline"
                                    color="primary.main"
                                >
                                    Pricing FAQ
                                </Typography>

                                <Typography
                                    id="pricing-faq-heading"
                                    variant="h2"
                                    sx={{
                                        mt: 1.5, mb: 2,
                                    }}
                                >
                                    Questions before you choose?
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                >
                                    Learn how trials, data, integrations and support work. For requirements specific to
                                    your organisation, contact the CibusAI team.
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                {faqs.map((faq) => (<FAQItem
                                        key={faq.question}
                                        {...faq}
                                    />))}
                            </Grid>
                        </Grid>
                    </Box>

                </Container>
            </Box>
        </>);
}